/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import {
  getConstants,
  decodeFromToken,
  formatAuthenticationScheme,
  isNull,
  isValue,
} from '../../helpers';
import { ApiException, ConfigException } from '../../exceptions';
import {
  ApiCredentialsType,
  ApiEndPointsNames,
  ApiOriginType,
  IFinalResponse,
  InitApiType,
  InitializeTypes,
  KeyValueStringType,
} from '../../types';
import { Bundle } from '../../util';
import { HeadersType } from '../../util/constants';
import { Logger } from '../logger';
import { configureMock } from '../mock_api';
import { configureAxiosIntLogger } from './AxiosInterceptor';
import { getEndPoints } from './Endpoints';
import { Auth0 } from '../../integration/auth';

class AxiosHelperClass {
  private static readonly instance: AxiosInstance = axios.create();

  async request(
    method: AxiosRequestConfig['method'],
    apiDomain: ApiEndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    const result = prepareUrlAndHeaderByType(
      apiDomain,
      path,
      init.pathParameters,
      init.pathUrl,
    );
    const options: AxiosRequestConfig = {
      method,
      url: result.url,
      withCredentials: init.withCredentials ?? false,
    };
    if (!isNull(init.body)) {
      options.data = init.body;
    }
    if (!isNull(init.params)) {
      options.params = init.params;
    }
    if (!isNull(init.headers)) {
      options.headers = init.headers;
    }
    if (!isNull(init.proxy)) {
      options.proxy = init.proxy;
    }

    if (result.header) {
      options.headers = { ...options.headers, ...result.header };
    }
    try {
      const headerAuthorization = await getHeaderAuthorization(
        credentialsType,
        result.type,
      );
      if (headerAuthorization !== null) {
        options.headers = { ...options.headers, ...headerAuthorization };
      }
    } catch (error: any) {
      Logger.error(error.message);
    }

    return AxiosHelperClass.instance.request(options);
  }

  public get(
    apiDomain: ApiEndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('GET', apiDomain, path, credentialsType, init);
  }
  public post(
    apiDomain: ApiEndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('POST', apiDomain, path, credentialsType, init);
  }
  public put(
    apiDomain: ApiEndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('PUT', apiDomain, path, credentialsType, init);
  }
  public delete(
    apiDomain: ApiEndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('DELETE', apiDomain, path, credentialsType, init);
  }
  public patch(
    apiDomain: ApiEndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('PATCH', apiDomain, path, credentialsType, init);
  }
  public configure(config: InitializeTypes): string[] {
    let lstMocksEnabled: string[] = [];
    try {
      configureAxios({ instance: AxiosHelperClass.instance, config });
    } catch (error: any) {
      Logger.error(error.message);
      throw new ConfigException({ message: error.message, resource: 'axios' });
    }
    try {
      lstMocksEnabled = configureMock(AxiosHelperClass.instance, config);
    } catch (error: any) {
      Logger.error(error.message);
      throw new ConfigException({ message: error.message, resource: 'mocks' });
    }
    return lstMocksEnabled;
  }
  public setAuthToken(token: string) {
    setAuthToken(AxiosHelperClass.instance, token);
  }
  public saveTokens(token: string, type: ApiCredentialsType) {
    Bundle.setValue(`TOKEN_${type}`, token);
  }
  public saveSmilesToken(token: string) {
    Bundle.setValue(HeadersType.smilesToken, token);
  }
  public removeTokens(...type: ApiCredentialsType[]) {
    type.forEach((element) => {
      Bundle.clearValue(`TOKEN_${element}`);
    });
  }
  public removeSmilesToken() {
    Bundle.clearValue(HeadersType.smilesToken);
  }
  public getMemberNumberByAuthUser = (): string | null => {
    let memberNumber = '';
    const key = Bundle.getValue('TOKEN_TOKEN_AUTH_USER');
    if (key) {
      const jsonAccessToken = JSON.parse(key);
      memberNumber = decodeFromToken(
        'https://smiles.api/memberNumber',
        jsonAccessToken.accessToken,
      );
    }
    return memberNumber;
  };
}

const setAuthToken = (pInstance: AxiosInstance, token: string) => {
  if (token) {
    pInstance.defaults.headers.common['Authorization'] = token;
  } else {
    delete pInstance.defaults.headers.common['Authorization'];
  }
};

const configureAxios = (params: {
  instance: AxiosInstance;
  config: InitializeTypes;
}) => {
  params.instance.defaults.timeout = params.config.service.timeout;

  const { axiosInterceptor } = params.config;

  params.instance.interceptors.request.use((config) => {
    if (axiosInterceptor?.request) {
      return axiosInterceptor.request(config);
    }
    return config;
  });

  params.instance.interceptors.response.use(
    (config) => {
      if (axiosInterceptor?.response?.success) {
        return axiosInterceptor.response.success(config);
      }
      return config;
    },
    (error) => {
      if (axiosInterceptor?.response?.error) {
        axiosInterceptor.response.error(error);
      }
      return Promise.reject(error);
    },
  );

  configureAxiosIntLogger(params.instance, params.config);
};

const prepareUrlAndHeaderByType = (
  apiName: ApiEndPointsNames,
  path: string,
  pathParameters: KeyValueStringType,
  pathUrl?: string | undefined | null,
): any => {
  const config = Bundle.getValue('config');
  if (config.debug) {
    // eslint-disable-next-line no-console
    console.debug(`AxiosHelpers.ts => prepareUrlAndHeaderByType: ${path}`);
  }
  const lstEndpoints = getEndPoints({
    pEvironment: config.service.environment,
  });
  const endpointFind = lstEndpoints.filter(
    (endPoint) => endPoint.name === apiName,
  );
  if (isNull(endpointFind) || endpointFind.length === 0) {
    throw new ApiException({
      statusCode: '999',
      errorCode: '990',
      errorMessage: 'Endpoint not found',
      message: '',
    });
  }

  let newPath = path;
  if (pathUrl) {
    newPath = pathUrl;
  }

  if (pathParameters && pathParameters !== null) {
    newPath = replacePathParameters(newPath, pathParameters);
  }

  const { endpoint, type } = endpointFind[0];

  if (endpoint.charAt(endpoint.length - 1) === '/') {
    if (newPath.charAt(0) === '/') {
      newPath = newPath.substring(1);
    }
  } else {
    if (newPath.charAt(0) !== '/') {
      newPath = `/${newPath}`;
    }
  }
  const url = `${endpoint}${newPath}`;
  let header = null;
  if (type === 'Smiles') {
    let channel;
    if (config.channel && config.channel.toUpperCase() === 'WEB') {
      channel = isMobile.any()
        ? 'Mobile'
        : config.channel.charAt(0).toUpperCase() +
          config.channel.toLowerCase().slice(1);
    } else {
      channel = config.channel;
    }

    header = {
      region: config.service.region,
      channel,
    };
  }
  return {
    url,
    header,
    type,
  };
};

const replacePathParameters = (
  path: string,
  pathParameters: KeyValueStringType,
) => {
  Object.entries(pathParameters || {}).forEach(([k, v]) => {
    path = path.replace(`:${k}`, encodeURIComponent(v));
  });
  return path;
};

const configureTokenAuth0 = async () => {
  try {
    const key = 'auth0MtoM';
    const keysAuth0 = getConstants(key);
    let auth0Result: IFinalResponse = {
      success: false,
      data: null,
      errors: undefined,
      metadata: null,
    };

    if (keysAuth0 && keysAuth0 !== null) {
      const request = {
        client_id: keysAuth0.client_id as string,
        client_secret: keysAuth0.client_secret as string,
        audience: keysAuth0.audience as string,
        grant_type: keysAuth0.grant_type as string,
      };
      auth0Result = await Auth0.call(request);
    }

    if (auth0Result.success) {
      Bundle.setValue(
        'TOKEN_TOKEN_CREDENTIALS',
        JSON.stringify(auth0Result.data),
      );
    } else {
      let msgError = '';
      let errorCode = '';
      if (auth0Result.errors && auth0Result.errors.length > 0) {
        const errorAuth0 = auth0Result.errors[0];
        msgError = errorAuth0?.errorMessage ? errorAuth0?.errorMessage : '';
        errorCode = errorAuth0?.errorCode
          ? errorAuth0?.errorCode?.toString()
          : '';
      }
      throw new ConfigException({
        message: msgError,
        errorCode: errorCode,
        resource: 'Auth0tokenCredentials',
      });
    }
  } catch (error: any) {
    Logger.error(error.message);
    throw new ConfigException({
      message: error.message,
      resource: 'Auth0tokenCredentials',
    });
  }
};

const getCredentials = async (credentialsType: ApiCredentialsType) => {
  let credentialsJson = Bundle.getValue(`TOKEN_${credentialsType}`);
  if (credentialsJson && credentialsJson !== null) {
    return JSON.parse(credentialsJson);
  } else if (credentialsType === 'TOKEN_CREDENTIALS') {
    await configureTokenAuth0();
    credentialsJson = Bundle.getValue(`TOKEN_${credentialsType}`);
    if (credentialsJson && credentialsJson !== null) {
      return JSON.parse(credentialsJson);
    }
  }
  return null;
};

const getHeaderAuthorization = async (
  credentialsType: ApiCredentialsType,
  apiType: ApiOriginType,
) => {
  const config = Bundle.getValue('config');
  if (config.debug) {
    // eslint-disable-next-line no-console
    console.debug(`AxiosHelpers.ts => getHeaderAuthorization`);
  }

  const isMockAuth =
    config.service.environment === 'MOCK' &&
    isValue(config.globalMocks?.authorization);

  let headerAuthorization = null;
  if (credentialsType === 'TOKEN_CREDENTIALS') {
    const credentials = await getCredentials(credentialsType);
    if (credentials) {
      headerAuthorization = {
        Authorization: formatAuthenticationScheme(
          isMockAuth
            ? config.globalMocks.authorization
            : credentials.access_token,
          apiType,
        ),
      };
    }
  } else if (credentialsType === 'TOKEN_AUTH_USER') {
    const credentials = await getCredentials(credentialsType);
    if (credentials) {
      headerAuthorization = {
        Authorization: formatAuthenticationScheme(
          isMockAuth
            ? config.globalMocks.authorization
            : credentials.accessToken,
          apiType,
        ),
      };
    }
  } else if (credentialsType === 'TOKEN_AUTH_USER_OR_CREDENTIALS') {
    let credentials = await getCredentials('TOKEN_AUTH_USER');
    if (credentials && credentials.accessToken) {
      headerAuthorization = {
        Authorization: formatAuthenticationScheme(
          isMockAuth
            ? config.globalMocks.authorization
            : credentials.accessToken,
          apiType,
        ),
      };
    } else {
      credentials = await getCredentials('TOKEN_CREDENTIALS');
      if (credentials) {
        headerAuthorization = {
          Authorization: formatAuthenticationScheme(
            credentials.access_token,
            apiType,
          ),
        };
      }
    }
  } else if (credentialsType === 'TOKEN_X_API_KEY') {
    headerAuthorization = {
      [HeadersType.xApiKey]: await getCredentials(credentialsType),
    };
  }
  return headerAuthorization;
};

const isMobile = {
  Android: function () {
    return navigator.userAgent.match(/Android/i);
  },
  BlackBerry: function () {
    return navigator.userAgent.match(/BlackBerry/i);
  },
  iOS: function () {
    return navigator.userAgent.match(/iPhone|iPod/i);
  },
  Opera: function () {
    return navigator.userAgent.match(/Opera Mini/i);
  },
  Windows: function () {
    return (
      navigator.userAgent.match(/IEMobile/i) ||
      navigator.userAgent.match(/WPDesktop/i)
    );
  },
  any: function () {
    if (typeof navigator === 'undefined') {
      return null;
    }
    return (
      isMobile.Android() ||
      isMobile.BlackBerry() ||
      isMobile.iOS() ||
      isMobile.Opera() ||
      isMobile.Windows()
    );
  },
};

const AxiosHelper = new AxiosHelperClass();
export default AxiosHelper;
